import { type AxiosResponse } from 'axios';
import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';

import type CarrierAccount from '~/types/CarrierAccount';
import type Rate from '~/types/Rate';
import type Shipment from '~/types/Shipment';

import { ActionKeys } from '~/actions/constants';
import {
  removeCheckoutRateAfterPollEntry,
  startCheckout,
  updateBatchEntriesServiceLevelToken,
} from '~/actions/orders.actions';
import * as ratesAtCheckoutActions from '~/actions/rates-at-checkout.actions';
import * as shipmentsActions from '~/actions/shipments.actions';
import { getAllOrdersV2 } from '~/modules/orders/utils/ordersV2.util';
import { LABEL_SETTINGS_QUERY_KEY } from '~/modules/settingsCarriers/constants/labelSettings.const';
import {
  type CheckoutRateAfterPollAttributes,
  type CheckoutRatesAfterPoll,
} from '~/reducers/orders/orders.reducer';
import { getOrdersTableData } from '~/selectors/batch-entry.composite.selectors';
import { getBatchEntryData } from '~/selectors/batch-entry.selectors';
import { getCarrierAccounts } from '~/selectors/carrier-accounts.selectors';
import { getCheckoutRateAfterPoll } from '~/selectors/orders.selectors';
import { getAllServiceLevelsMapByAccount } from '~/selectors/providers.selectors';
import { getAllRatesData, getUserTouchedRates } from '~/selectors/rates.selectors';
import {
  getIsShippingOptionsRequestComplete,
  getIsShippingOptionsRequestInitiated,
  getShippingOptions,
} from '~/selectors/rates-at-checkout.selectors';
import { getShipmentsData } from '~/selectors/shipments.selectors';
import * as ratesAtCheckoutService from '~/services/rates-at-checkout.service';
import settingsService from '~/services/settings.service';
import * as shipmentsService from '~/services/shipments.service';
import { labelSettingsFromJSON, type JSONLabelSetting } from '~/types/LabelSettings';
import { type OrdersRenderData } from '~/types/Order';
import { type BatchEntryExtended } from '~/types/OrderBatchEntry';
import { type ObjectIdProviderAccountMap } from '~/types/ProviderAccount';
import {
  attributePopularRateV2,
  getRatesByAttribute,
  getRatesForServiceLevelToken,
  getRatesWithAttributeInDisplayOrder,
} from '~/types/Rate';
import {
  findCheapestMatchedServiceLevelRate,
  getMatchedShippingOption,
  getShipmentDatasetById,
  isIntegratedStore,
  type ShippingOptionPayload,
} from '~/types/RatesAtCheckout';
import * as shipmentModel from '~/types/Shipment';
import { ShipmentStatus } from '~/types/Shipment';
import { defaultQueryClient } from '~/utils/ShippoQueryClientProvider';
import { trackingHelper } from '~/utils/TrackingHelper';
import { RATE_DISPLAYED_EVENT, RATES_UPDATED_EVENT } from '~/utils/TrackingHelper/types';
import {
  getRateDisplayedEventData,
  trackRateDisplay,
  trackRateUpdate,
} from '~/utils/TrackingHelper/utils';
import { FeatureFlags, decideFeatureFlag } from '~/utils/featureflags';
import { observability } from '~/utils/observability';
import { getBestRates } from '~/utils/rates';
import { batchAction } from '~/utils/redux/batch';

import { selectState } from '..';

export function* fetchShipment(action: ReturnType<typeof shipmentsActions.fetchShipment>) {
  try {
    const { shipmentId, trackingReference } = action;
    const response: AxiosResponse<shipmentModel.JSONShipment> = yield call(
      shipmentsService.getRates,
      shipmentId,
    );
    const shipment: Shipment = shipmentModel.fromJSON(response.data);

    shipment.rates = getBestRates(shipment.rates);

    const payload: shipmentsActions.FetchShipmentPayload = {
      shipment,
    };

    yield call(trackRateUpdate, trackingReference, RATES_UPDATED_EVENT.operations.GENERATED, {
      rateIds: payload.shipment?.rates?.map((rate) => rate.objectId),
      shipmentId: payload.shipment?.objectId,
    });

    yield call(recalculateHiddenRates, shipment);

    yield put(
      batchAction(
        shipmentsActions.fetchShipmentRatesSuccess(shipmentId, payload, trackingReference),
      ),
    );
  } catch (err) {
    yield put(shipmentsActions.fetchShipmentRatesError(action.shipmentId));
  }
}

export function* autoSelectRate(
  action: ReturnType<typeof shipmentsActions.fetchShipmentRatesSuccess>,
) {
  const {
    payload: { shipment },
    trackingReference,
  } = action;
  const defaultShippingOptions = yield call(getDefaultShippingOptionsFromCache);
  const shipmentObjectId = shipment.objectId;

  // Check that the shipment hasn't been "touched" by the user
  const userTouchedRates: Record<string, boolean> = yield selectState(getUserTouchedRates);
  if (userTouchedRates[shipmentObjectId]) return;

  const {
    checkoutRateEntry,
    checkoutRateKey,
    isIntegratedStoreOrder,
    isReturn,
    orderShipmentMethod,
    userSelectedAccountObjectID,
    userSelectedServiceLevelToken,
  } = yield call(getDetailsForAutoRateSelection, shipmentObjectId);

  const selectedRate: Rate | undefined = yield* determineSelectedRate({
    defaultShippingOptions,
    filteredRates: shipment.rates,
    isIntegratedStoreOrder,
    isReturn,
    orderShipmentMethod,
    userSelectedAccountObjectID,
    userSelectedServiceLevelToken,
  });

  if (!selectedRate) return;

  /**
   * True only when we actually re-selected the rate the user had picked before the address bypass.
   * Match BOTH service level token and carrier account;
   * if either doesn't match, we fall back to the default rate selection logic.
   */
  const isRestore =
    !!checkoutRateEntry?.currBatchEntry &&
    selectedRate.servicelevel.token === checkoutRateEntry.serviceLevelToken &&
    selectedRate.carrierAccount === checkoutRateEntry.carrierAccount;

  const lockRestoredRate = isRestore && checkoutRateEntry?.autoStartCheckout === false;

  yield put(
    batchAction(
      shipmentsActions.selectRate(
        shipmentObjectId,
        selectedRate.objectId,
        lockRestoredRate,
        trackingReference,
      ),
    ),
  );

  if (!checkoutRateEntry?.currBatchEntry) return;

  if (checkoutRateEntry.autoStartCheckout) {
    yield put(
      startCheckout({
        [checkoutRateEntry.currBatchEntry.orderObjectId]: [checkoutRateEntry.currBatchEntry.id],
      }),
    );
  } else if (isRestore && checkoutRateKey) {
    yield put(removeCheckoutRateAfterPollEntry(checkoutRateKey));
  }
}

type DefaultShippingOptions = {
  fallbackAccountObjectId: null | string | undefined;
  fallbackServiceLevelToken: null | string | undefined;
  outboundAccountObjectId: null | string;
  outboundServiceLevelToken: string;
  returnAccountObjectId: null | string | undefined;
  returnServiceLevelToken: null | string | undefined;
};

// We save the objectIds as uuids with hyphens in the query data;
// the carrierAccount value from the rates API is a uuid with no hyphens
const removeDashes = (uuid: null | string | undefined): null | string =>
  uuid?.replace(/-/g, '') ?? null;

const getDefaultShippingOptionsFromCache = async (): Promise<DefaultShippingOptions | null> => {
  try {
    const cachedResponse = await defaultQueryClient.ensureQueryData<{ data: JSONLabelSetting }>({
      queryFn: settingsService.fetchLabelSettings,
      queryKey: LABEL_SETTINGS_QUERY_KEY,
    });
    if (!cachedResponse?.data) return null;

    const labelSettings = labelSettingsFromJSON(cachedResponse.data);

    if (!labelSettings.outboundServiceLevel) return null;

    return {
      fallbackAccountObjectId: removeDashes(labelSettings.outboundFallbackAccount),
      fallbackServiceLevelToken: labelSettings.outboundFallbackServiceLevel,
      outboundAccountObjectId: removeDashes(labelSettings.defaultOutboundAccount),
      outboundServiceLevelToken: labelSettings.outboundServiceLevel,
      returnAccountObjectId: removeDashes(labelSettings.defaultReturnAccount),
      returnServiceLevelToken: labelSettings.defaultReturnServiceLevel,
    };
  } catch (error) {
    observability.errorEvent('FAILED_TO_FETCH_DEFAULT_SHIPPING_OPTIONS', {
      errorMessage: `Failed to fetch default shipping options: ${error}`,
      force: true,
    });
    return null;
  }
};

/**
 * Gathers all details required to determine which rate is to be auto-selected for a given
 * order shipment.
 */
function* getDetailsForAutoRateSelection(shipmentObjectId: string) {
  const ordersV2TableFlag = decideFeatureFlag(FeatureFlags.ORDERS_TABLE_API_V2_UI);

  const checkoutRatesAfterPoll: Maybe<CheckoutRatesAfterPoll> =
    yield select(getCheckoutRateAfterPoll);
  const [checkoutRateKey, checkoutRateEntry]: [
    string | undefined,
    Maybe<CheckoutRateAfterPollAttributes>,
  ] = Object.entries(checkoutRatesAfterPoll ?? {}).find(
    ([, entry]) => entry?.currBatchEntry?.shipmentObjectId === shipmentObjectId,
  ) ?? [undefined, null];

  if (ordersV2TableFlag?.enabled) {
    const ordersV2 = getAllOrdersV2();
    const order = ordersV2.find((order) =>
      order.order_shipments.some((shipment) => shipment.shipment_object_id === shipmentObjectId),
    );
    const orderShipment = order?.order_shipments.find(
      (shipment) => shipment.shipment_object_id === shipmentObjectId,
    );

    const getOrdersV2UserSelectedRate = function* () {
      if (orderShipment?.preferred_service_level?.account_object_id === undefined) {
        const batchEntries: HashMap<number, BatchEntryExtended> = yield select(getBatchEntryData);
        const batchEntryWithPreferredServiceLevelToken = Object.values(batchEntries).find(
          (batchEntry: BatchEntryExtended) =>
            batchEntry.shipmentObjectId === shipmentObjectId &&
            batchEntry.preferredServiceLevelToken,
        );

        return getUserSelectedRate(
          {
            objectId: shipmentObjectId,
            preferredAccountObjectID:
              batchEntryWithPreferredServiceLevelToken?.preferredAccountObjectID,
            preferredServiceLevelToken:
              batchEntryWithPreferredServiceLevelToken?.preferredServiceLevelToken,
          },
          checkoutRateEntry,
        );
      }

      return getUserSelectedRate(
        {
          objectId: shipmentObjectId,
          preferredAccountObjectID: orderShipment.preferred_service_level.account_object_id,
          preferredServiceLevelToken: orderShipment.preferred_service_level.token,
        },
        checkoutRateEntry,
      );
    };

    const { userSelectedAccountObjectID, userSelectedServiceLevelToken } =
      yield* getOrdersV2UserSelectedRate();

    return {
      checkoutRateEntry,
      checkoutRateKey,
      isIntegratedStoreOrder: isIntegratedStore(order?.store?.name),
      isReturn: orderShipment?.is_return ?? false,
      orderShipmentMethod: order?.customer_selected_shipping?.method,
      userSelectedAccountObjectID,
      userSelectedServiceLevelToken,
    };
  }

  const batchEntries: HashMap<number, BatchEntryExtended> = yield select(getBatchEntryData);
  const batchEntryForShipment = Object.values(batchEntries).find(
    (batchEntry: BatchEntryExtended) => batchEntry.shipmentObjectId === shipmentObjectId,
  );
  const batchEntryWithPreferredServiceLevelToken = Object.values(batchEntries).find(
    (batchEntry: BatchEntryExtended) =>
      batchEntry.shipmentObjectId === shipmentObjectId && batchEntry.preferredServiceLevelToken,
  );

  const shipmentDatasets: OrdersRenderData[] = yield select(getOrdersTableData);
  const shipmentDataset: Maybe<OrdersRenderData> = getShipmentDatasetById(
    shipmentObjectId,
    shipmentDatasets,
  );

  const { userSelectedAccountObjectID, userSelectedServiceLevelToken } = getUserSelectedRate(
    {
      objectId: shipmentObjectId,
      preferredAccountObjectID: batchEntryWithPreferredServiceLevelToken?.preferredAccountObjectID,
      preferredServiceLevelToken:
        batchEntryWithPreferredServiceLevelToken?.preferredServiceLevelToken,
    },
    checkoutRateEntry,
  );

  return {
    checkoutRateEntry,
    checkoutRateKey,
    isIntegratedStoreOrder: isIntegratedStore(shipmentDataset?.order?.shopApp),
    isReturn: batchEntryForShipment?.isReturn ?? false,
    orderShipmentMethod: shipmentDataset?.order?.shippingMethod,
    userSelectedAccountObjectID,
    userSelectedServiceLevelToken,
  };
}

/**
 * Determines the user-selected rate details for a given shipment.
 *
 * "Checkout rate after poll" takes precedence because it handles and edge case where the
 * transaction fails due to an invalid address. In this case, the UI creates a new batch
 * entry and this function will ensure that an equivalent rate is selected for the new
 * batch entry.
 */
const getUserSelectedRate = (
  shipment: {
    objectId: string;
    preferredAccountObjectID: null | string | undefined;
    preferredServiceLevelToken: null | string | undefined;
  },
  checkoutRateAfterPoll: CheckoutRateAfterPollAttributes | null | undefined,
) => {
  if (
    shipment.objectId &&
    checkoutRateAfterPoll?.currBatchEntry?.shipmentObjectId === shipment.objectId
  ) {
    return {
      userSelectedAccountObjectID: checkoutRateAfterPoll.carrierAccount,
      userSelectedServiceLevelToken: checkoutRateAfterPoll.serviceLevelToken,
    };
  }

  return {
    userSelectedAccountObjectID: shipment.preferredAccountObjectID,
    userSelectedServiceLevelToken: shipment.preferredServiceLevelToken,
  };
};

/**
 * Finds the rate matching the default shipping configuration (return or outbound + fallback).
 */
const findRateForDefaultConfiguration = ({
  bestOption,
  cheapestAndFastest,
  cheapestRates,
  defaultShippingOptions,
  filteredRates,
  isReturn,
}: {
  bestOption: Rate[];
  cheapestAndFastest: Rate[];
  cheapestRates: Rate[];
  defaultShippingOptions: DefaultShippingOptions;
  filteredRates: Rate[];
  isReturn: boolean;
}): Rate | undefined => {
  const attributeRates: Record<string, Rate | undefined> = {
    best: bestOption[0],
    cheapest: cheapestRates[0],
    fastest: cheapestAndFastest[0],
  };

  // Default return configuration
  if (isReturn && defaultShippingOptions.returnServiceLevelToken) {
    const returnMatch =
      attributeRates[defaultShippingOptions.returnServiceLevelToken] ??
      getRatesForServiceLevelToken(
        filteredRates,
        defaultShippingOptions.returnServiceLevelToken,
        defaultShippingOptions.returnAccountObjectId ?? undefined,
      )[0];
    if (returnMatch) return returnMatch;
  }

  // Default outbound configurations
  if (!isReturn) {
    // Primary outbound configuration
    const primaryMatch =
      attributeRates[defaultShippingOptions.outboundServiceLevelToken] ??
      getRatesForServiceLevelToken(
        filteredRates,
        defaultShippingOptions.outboundServiceLevelToken,
        defaultShippingOptions.outboundAccountObjectId ?? undefined,
      )[0];
    if (primaryMatch) return primaryMatch;

    // Fallback outbound configuration
    if (
      defaultShippingOptions.outboundServiceLevelToken === 'best' &&
      defaultShippingOptions.fallbackServiceLevelToken
    ) {
      const fallbackMatch =
        attributeRates[defaultShippingOptions.fallbackServiceLevelToken] ??
        getRatesForServiceLevelToken(
          filteredRates,
          defaultShippingOptions.fallbackServiceLevelToken,
          defaultShippingOptions.fallbackAccountObjectId ?? undefined,
        )[0];
      if (fallbackMatch) return fallbackMatch;
    }
  }

  // No matches for default configurations
  return undefined;
};

/**
 * Determines the rate that should be auto-selected for a given order shipment.
 */
function* determineSelectedRate({
  defaultShippingOptions,
  filteredRates,
  isIntegratedStoreOrder,
  isReturn,
  orderShipmentMethod,
  userSelectedAccountObjectID,
  userSelectedServiceLevelToken,
}: {
  defaultShippingOptions: DefaultShippingOptions | null;
  filteredRates: Rate[];
  isIntegratedStoreOrder?: boolean;
  isReturn?: boolean;
  orderShipmentMethod?: string;
  userSelectedAccountObjectID?: string;
  userSelectedServiceLevelToken?: string;
}) {
  if (filteredRates.length === 0) return;

  /**
   * If any order shipments associated with this shipment have a preferred service level,
   * check if any rates match up with them
   */
  if (userSelectedServiceLevelToken) {
    const ratesForServiceLevelToken: Rate[] = getRatesForServiceLevelToken(
      filteredRates,
      userSelectedServiceLevelToken,
      userSelectedAccountObjectID,
    );
    if (ratesForServiceLevelToken.length > 0) return ratesForServiceLevelToken[0];
  }

  // Find match for shipping option service level token to returned rates
  if (isIntegratedStoreOrder && orderShipmentMethod) {
    const cheapestMatchedRate = yield* findRateForIntegratedOrder({
      filteredRates,
      orderShipmentMethod,
    });
    if (cheapestMatchedRate) return cheapestMatchedRate;
  }

  const { bestOption, cheapestAndFastest, cheapestRates } = getRatesByAttribute(filteredRates);

  if (defaultShippingOptions) {
    const defaultMatchedRate = findRateForDefaultConfiguration({
      bestOption,
      cheapestAndFastest,
      cheapestRates,
      defaultShippingOptions,
      filteredRates,
      isReturn: isReturn ?? false,
    });
    if (defaultMatchedRate) return defaultMatchedRate;
  }

  // Precedence: best option > cheapest > cheapest and fastest
  if (bestOption.length > 0) return bestOption[0];
  if (cheapestRates.length > 0) return cheapestRates[0];
  if (cheapestAndFastest.length > 0) return cheapestAndFastest[0];

  // No auto selected rates observability event
  observability.errorEvent('AUTO_SELECT_RATES', {
    errorMessage: `No auto selected rates, choosing first item of list`,
    force: true,
    rates: filteredRates.map((rate) => ({
      amount: rate.amount,
      attributes: rate.attributes.join(','),
      provider: rate.provider,
      servicelevelName: rate.servicelevel.name,
    })),
  });

  // Fallback, just return the first rate
  return filteredRates[0];
}

/**
 * Finds the rate that matches the shipping option for an integrated store order.
 */
function* findRateForIntegratedOrder({
  filteredRates,
  orderShipmentMethod,
}: {
  filteredRates: Rate[];
  orderShipmentMethod: string;
}) {
  let shippingOptions: ShippingOptionPayload[] =
    yield selectState<ShippingOptionPayload[]>(getShippingOptions);
  const isShippingOptionsInitiated: boolean = yield selectState<boolean>(
    getIsShippingOptionsRequestInitiated,
  );
  const isShippingOptionsRequestComplete: boolean = yield selectState<boolean>(
    getIsShippingOptionsRequestComplete,
  );

  // Fetch shipping options if we haven't already
  if (
    (shippingOptions.length === 0 && !isShippingOptionsInitiated) ||
    !isShippingOptionsRequestComplete
  ) {
    try {
      const response: { data: ShippingOptionPayload[] } = yield call(
        ratesAtCheckoutService.fetchShippingOptions,
      );

      yield put(ratesAtCheckoutActions.fetchShippingOptionsSuccess(response));
      shippingOptions = yield selectState<ShippingOptionPayload[]>(getShippingOptions);
    } catch (err) {
      yield put(ratesAtCheckoutActions.fetchShippingOptionsError(err.message));
    }
  }

  // Find if there's a matching shipment option for the order
  const matchedShippingOption = getMatchedShippingOption(shippingOptions, orderShipmentMethod);

  /**
   * Find a match for shipping option service level token to rate.
   * If multiple matched, return the cheapest rate.
   */
  return findCheapestMatchedServiceLevelRate(matchedShippingOption?.service_levels, filteredRates);
}

const getHiddenRates = ({
  allServiceLevels,
  rates,
}: {
  allServiceLevels: ObjectIdProviderAccountMap;
  rates: Rate[];
}) =>
  rates
    .filter((rate) => {
      if (!rate?.carrierAccount) return false;
      if (!rate?.servicelevel?.token) return false;

      const serviceLevel =
        allServiceLevels[rate.carrierAccount]?.serviceLevelsByToken[
          rate.servicelevel.extended_token ?? rate.servicelevel.token
        ];

      return serviceLevel?.is_hidden;
    })
    .map((rate) => rate.objectId);

/**
 * Recalculates hidden rates and auto-selects rate for a given shipment
 */
function* recalculateHiddenRates(shipment: Shipment) {
  const allServiceLevels: ReturnType<typeof getAllServiceLevelsMapByAccount> = yield selectState(
    getAllServiceLevelsMapByAccount,
  );

  const hiddenRates = getHiddenRates({ allServiceLevels, rates: shipment.rates });
  yield put(shipmentsActions.appendHiddenRates(hiddenRates));

  const trackingReference = trackingHelper.getTrackingReference(
    RATES_UPDATED_EVENT.name,
    'order_table',
    { rateIds: undefined },
  );

  yield call(autoSelectRate, {
    payload: {
      shipment: {
        ...shipment,
        rates: shipment.rates.filter((rate) => !hiddenRates.includes(rate.objectId)),
      },
    },
    shipmentId: shipment.objectId,
    trackingReference,
    type: ActionKeys.SHIPMENT_FETCH_SUCCESS,
  });
}

/**
 * Recalculates hidden rates and auto-selects rates for all previously-fetched shipments
 */
function* recalculateHiddenRatesForAllShipments() {
  const allServiceLevels: ReturnType<typeof getAllServiceLevelsMapByAccount> = yield selectState(
    getAllServiceLevelsMapByAccount,
  );
  const allRatesData: ReturnType<typeof getAllRatesData> = yield select(getAllRatesData);

  const hiddenRates = getHiddenRates({ allServiceLevels, rates: Object.values(allRatesData) });
  yield put(shipmentsActions.setHiddenRates(hiddenRates));

  const trackingReference = trackingHelper.getTrackingReference(
    RATES_UPDATED_EVENT.name,
    'order_table',
    { rateIds: undefined },
  );

  const shipmentsData: ReturnType<typeof getShipmentsData> = yield selectState(getShipmentsData);
  for (const shipment of Object.values(shipmentsData)) {
    yield call(autoSelectRate, {
      payload: { shipment },
      shipmentId: shipment.objectId,
      trackingReference,
      type: ActionKeys.SHIPMENT_FETCH_SUCCESS,
    });
  }
}

export function* selectRate(action: ReturnType<typeof shipmentsActions.selectRate>) {
  const {
    accountObjectId,
    orderSelection,
    rateObjectId,
    serviceLevelToken,
    trackingReference,
    userTriggered,
  } = action.payload;

  trackRateUpdate(trackingReference, RATES_UPDATED_EVENT.operations.SELECTED, {
    isAutoSelection: !userTriggered,
    rateId: rateObjectId,
  });

  if (userTriggered && orderSelection && serviceLevelToken) {
    yield put(
      updateBatchEntriesServiceLevelToken(orderSelection, serviceLevelToken, accountObjectId),
    );
  }
}

function* selectRates(action: ReturnType<typeof shipmentsActions.selectRate>) {
  const { accountObjectId, orderSelection, serviceLevelToken, trackingReference } = action.payload;
  if (orderSelection && serviceLevelToken) {
    trackRateUpdate(trackingReference, RATES_UPDATED_EVENT.operations.SELECTED, {
      isAutoSelection: false, // selecting multiple rates only possible by user trigger
    });
    yield put(
      updateBatchEntriesServiceLevelToken(orderSelection, serviceLevelToken, accountObjectId),
    );
  }
}

export function* trackRateDisplayed(
  action: ReturnType<typeof shipmentsActions.fetchShipmentRatesSuccess>,
) {
  const {
    payload: { shipment },
    trackingReference,
  } = action;

  const { isBulk, orderQuantity } = trackingReference;

  if (shipment.status !== ShipmentStatus.SUCCESS || isBulk || !orderQuantity) return;

  const { rates } = shipment;

  // update attributes of rates before sorting
  const attributedRates = attributePopularRateV2(rates);

  // get rates in order how they are displayed in UI
  const sortedRates = getRatesWithAttributeInDisplayOrder(attributedRates);
  const carrierAccounts: CarrierAccount[] = yield select(getCarrierAccounts);

  // track each displayed rate
  for (const rate of attributedRates) {
    const eventData = getRateDisplayedEventData(rate, sortedRates, carrierAccounts);
    yield call(
      trackRateDisplay,
      trackingReference,
      RATE_DISPLAYED_EVENT.operations.DISPLAYED,
      eventData,
    );
  }
}

export default [
  takeEvery(ActionKeys.SHIPMENT_FETCH, fetchShipment),
  takeLatest(ActionKeys.SHIPMENT_FETCH_SUCCESS, trackRateDisplayed),
  takeEvery(ActionKeys.SHIPMENT_RECALCULATE_HIDDEN_RATES, recalculateHiddenRatesForAllShipments),
  takeLatest(ActionKeys.RATE_SELECT, selectRate),
  takeLatest(ActionKeys.MULTIPLE_RATE_SELECT, selectRates),
];
